import { SimpleSearchFilter } from 'api/dashboard/widget';
import { MemberType, Team, User } from 'api/models';
import { ProjectPhase } from 'api/project';
import {
  AllocationMedium,
  AllocationSourceTask,
  ResourceType,
} from 'api/resource-management/types';
import { Space } from 'api/space/space';
import { Task } from 'api/tasks';
import { Filter } from 'components/molecules/filters/types';
import { SpaceTab } from 'data/space-tabs';
import {
  BaseTemplate,
  TemplateId,
  TemplateObjectType,
} from 'data/templates/api';
import { NormalizedProjectTemplatePayload } from './api/transform';

type TypeKeyDecider<K, D> = K extends 'api' ? D : number;

export enum ProjectTemplateSpaceEnumType {
  ProjectSpaceValue = 'PROJECT_SPACE',
  SpaceTabValue = 'SPACE_TAB',
}

interface BaseProjectTemplateTask
  extends Omit<Task, 'startDate' | 'dueDate' | 'parentTask'> {
  parentTask:
    | {
        taskId: number;
      }
    | any;
}

export interface ProjectTemplateTask extends BaseProjectTemplateTask {
  formTemplateId: number;
  formTemplateDeleted: boolean;
  duration: number;
  projectPhase: ProjectPhase;
  startDay: number;
  subTasks: ProjectTemplateTask[];
  isNew?: boolean;
  responsibleOn?: MemberType[];
  filters?: SimpleSearchFilter;
  isTemplate?: boolean;
  isRecurring?: boolean;
}

export interface ProjectTemplateProjectPhase
  extends Omit<
    ProjectPhase,
    'project' | 'progressStatus' | 'startDate' | 'dueDate' | 'fields'
  > {
  duration: number;
  startDay: number;
  tasks: ProjectTemplateTask[];
  isNew?: boolean;
  filters?: SimpleSearchFilter;
  shiftDays?: number;
}

export interface ProjectTemplateSpaceTab
  extends Omit<SpaceTab, 'commentThreadId'> {
  filters?: SimpleSearchFilter;
}

export interface ProjectTemplateSpace
  extends Omit<Space, 'projectId' | 'project' | 'spaceTabs'> {
  isNew?: boolean;
  spaceTabs: AtLeast<
    ProjectTemplateSpaceTab,
    'spaceTabId' | 'spaceTabTitle' | 'spaceTabType'
  >[];
  filters?: SimpleSearchFilter;
}
interface BaseAllocation {
  id: number;
  startDay: number;
  duration: number;
  minutes: number;
  projectPhaseId?: number;
  billable: boolean;
  medium: AllocationMedium;
  sources?: AllocationSourceTask[];
}
export interface UserAllocation extends BaseAllocation {
  type: ResourceType.User;
  userId: number;
}

export interface TeamAllocation extends BaseAllocation {
  type: ResourceType.Team;
  teamId: number;
}

export type TemplateAllocation = UserAllocation | TeamAllocation;
export interface AllocatedMembers {
  users: User[];
  teams: Team[];
}

export enum BudgetedHoursCalculationMode {
  Auto = 'auto',
  Manual = 'manual',
}
type BudgetedHoursConfigAuto = {
  mode: BudgetedHoursCalculationMode.Auto;
};
type BudgetedHoursConfigManual = {
  mode: BudgetedHoursCalculationMode.Manual;
  value: number;
};

export type BudgetedHoursConfig =
  | BudgetedHoursConfigAuto
  | BudgetedHoursConfigManual;

export type TemplateContent<T> = {
  duration: number;
  private: boolean;
  startDay: number;
  projectPhases: TypeKeyDecider<T, ProjectTemplateProjectPhase>[];
  spaceList: ProjectTemplateSpace[];
  budgetedHours?: number;
  allocations?: TemplateAllocation[];
  allocatedMembers?: AllocatedMembers;
  autoAllocation?: boolean;
};

type TemplateMeta = {
  fieldFilters?: Omit<SimpleSearchFilter, 'match'>;
  assignee: {
    teams: Team[];
  };
  compressTimeline?: boolean;
  conditionalVariables?: any;
};

interface BaseProjectTemplate<T> extends BaseTemplate {
  objectType: TemplateObjectType.Project;
  templateContent: TemplateContent<T>;
  templateMeta?: TemplateMeta;
  compressTimeline?: boolean;
  autoBudgetedHours?: boolean;
}

export type ProjectTemplateApi = BaseProjectTemplate<'api'>;
export type ProjectTemplate = BaseProjectTemplate<number>;

export type ProjectTemplateActiveRecord = {
  id: number;
  key: string;
  type: 'PROJECT_PHASE' | 'TASK';
};

export type ProjectTemplateSpaceSetupStage =
  | 'YET_TO_START'
  | 'IN_PROGRESS'
  | 'COMPLETE';

export interface ProjectTemplateStoreRecord
  extends NormalizedProjectTemplatePayload {
  error: string | null;
  loading: boolean;
  spaceSetup: ProjectTemplateSpaceSetupStage;
  isSaving?: boolean;
}

export type ProjectTemplateStore = {
  activeId: TemplateId;
  projectToTemplate: boolean;
  activeRecord?: ProjectTemplateActiveRecord;
  activeSpaceId: number | null;
  activeSpaceTabId: number | null;
  data: {
    [key: string]: ProjectTemplateStoreRecord;
  };
};

interface ProjectTemplateBaseData {
  children?: ProjectTemplateTaskData[];
  duration: number;
  id: number;
  key: string;
  level: number;
  title: string;
  private: boolean;
  startDay: number;
  rootIndex: number;
  parentTotal: number;
  hierarchy?: string[];
}

export interface ProjectTemplatePhaseData extends ProjectTemplateBaseData {
  type: 'PROJECT_PHASE';
  data: ProjectTemplateProjectPhase;
  insertAt?: {
    id: number;
    direction: InsertDirection;
  };
}

export interface ProjectTemplateTaskData extends ProjectTemplateBaseData {
  type: 'TASK';
  data: ProjectTemplateTask;
}

export type ProjectTemplateData =
  | ProjectTemplatePhaseData
  | ProjectTemplateTaskData;

export type ProjectTemplateGanttData = ProjectTemplateData & {
  type: 'TASK' | 'PROJECT_PHASE';
  startDate?: string;
  endDate?: string;
  expanded?: boolean;
  manuallyScheduled?: boolean;
  actualDuration?: number;
};

export enum InsertDirection {
  Above = 0,
  Below = 1,
}

export type DynamicTemplateConditions = {
  [key: string]: Filter;
};

export type ProjectTemplateSpaceTabData = {
  id: number;
  key: string;
  title: string;
  type: ProjectTemplateSpaceEnumType.SpaceTabValue;
  level: number;
  parentTotal: number;
  parentId: number;
  private: boolean;
  data: AtLeast<
    ProjectTemplateSpaceTab,
    'spaceTabId' | 'spaceTabType' | 'spaceTabTitle'
  >;
  conditionRequired: boolean;
};

export type ProjectTemplateSpaceNameData = {
  key: string;
  id: number;
  title: string;
  level: number;
  type: ProjectTemplateSpaceEnumType.ProjectSpaceValue;
  private: boolean;
  data: ProjectTemplateSpace;
  rootIndex: number;
  parentTotal: number;
  parentId: number;
  children: ProjectTemplateSpaceTabData[];
  conditionRequired: boolean;
};

export type ProjectTemplateSpaceData =
  | ProjectTemplateSpaceNameData
  | ProjectTemplateSpaceTabData;
